import { observer } from "mobx-react-lite";
import { DatabaseIcon, ScissorsIcon } from "lucide-react";

import { RegexExtractionConfiguration } from "@/types/evaluate";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SingleSourceSelector } from "@/components/PowerBlock/Block/SourceSelectors";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required, validRegex } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";
import { Input } from "@/components/ui/input";

const validationSchema = createBlockSchema<RegexExtractionConfiguration>()
  .requireName()
  .addRule(validRegex("configuration.regex_pattern", "Must be a valid regular expression"))
  .addRule(required("configuration.source", "Source is required"))
  .build();

const RegexExtractionBlockCore = observer(
  ({
    sources = createEmptySources(),
  }: Omit<BlockProps<RegexExtractionConfiguration>, "DefaultFields">) => {
    const block = useBlock<RegexExtractionConfiguration>();
    const isReadonly = useIsReadonly();

    const handleRegexPatternChange = (pattern: string) => {
      block.setConfiguration({
        ...block.configuration,
        regex_pattern: pattern,
        source: block.configuration?.source || "",
      });
    };

    return (
      <>
        <SectionGroup
          title="Pattern Configuration"
          description="Configure the regular expression pattern for extraction"
          icon={<ScissorsIcon className="h-4 w-4" />}
        >
          <ValidatedField name="Regex Pattern" path="configuration.regex_pattern">
            <Input
              className="w-full"
              value={block.configuration?.regex_pattern || ""}
              onChange={e => handleRegexPatternChange(e.target.value)}
              placeholder="[A-Za-z0-9]"
              disabled={isReadonly}
            />
          </ValidatedField>
        </SectionGroup>

        <SectionGroup
          title="Data Source"
          description="Select the source to extract text from"
          icon={<DatabaseIcon className="h-4 w-4" />}
        >
          <SingleSourceSelector
            sources={sources}
            sourceKey="source"
            validationPath="configuration.source"
            name="Source"
          />
        </SectionGroup>
      </>
    );
  },
);

export default withBlockStore<RegexExtractionConfiguration>(RegexExtractionBlockCore, {
  validationSchema,
});
